import type { Control } from "react-hook-form";
import {
	ClientPropertyBuildingDispositionEnum,
	ClientPropertyBuildingMaterialEnum,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	BuildingFormInput,
	BuildingFormOutput,
} from "web_myplann/Portfolio/Forms/BuildingShared/buildingFormSchema";
import type { BuildingRealEstateType } from "web_myplann/Portfolio/Forms/RealEstateForm/realEstateTypes";
import { BUILDING_OPTIONAL_PARAMS } from "web_myplann/Portfolio/Helpers/buildingOptionalParams";
import { useBooleanGroupOptions } from "web_myplann/Portfolio/Hooks/useBooleanGroupOptions";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { useEnumSelectOptions } from "web_myplann/Shared/Hooks/useEnumSelectOptions";
import { UNITS } from "web_myplann/Shared/Utils/units";

type Props = {
	control: Control<BuildingFormInput, unknown, BuildingFormOutput>;
	type: BuildingRealEstateType;
};

const PropertyResources = Resources.PortfolioDetail.Property;
const FormResources = Resources.Portfolio.AddRealEstate;

export const BuildingParameterFields = ({ control, type }: Props) => {
	const { t } = useResource();

	const dispositionOptions = useEnumSelectOptions(
		ClientPropertyBuildingDispositionEnum,
		PropertyResources.DispositionValue,
		[ClientPropertyBuildingDispositionEnum.None],
	);
	const buildingMaterialOptions = useEnumSelectOptions(
		ClientPropertyBuildingMaterialEnum,
		PropertyResources.BuildingMaterialValue,
		[
			ClientPropertyBuildingMaterialEnum.None,
			ClientPropertyBuildingMaterialEnum.Empty,
			ClientPropertyBuildingMaterialEnum.Custom,
		],
	);
	const amenityOptions = useBooleanGroupOptions(BUILDING_OPTIONAL_PARAMS);

	const showDisposition = type !== ClientPropertyTypeEnum.Cottage;
	const showHouseholdArea = type === ClientPropertyTypeEnum.Flat;
	const showLandSize = type !== ClientPropertyTypeEnum.Flat;

	return (
		<>
			<FlatRowFormInput
				control={control}
				name="floorArea"
				type="number"
				label={t(PropertyResources.FloorArea)}
				placeholder={t(FormResources.Placeholders.fill)}
				suffix={UNITS.squareMeter}
			/>
			{showHouseholdArea && (
				<FlatRowFormInput
					control={control}
					name="householdArea"
					type="number"
					label={t(PropertyResources.HouseholdArea)}
					placeholder={t(FormResources.Placeholders.fill)}
					suffix={UNITS.squareMeter}
				/>
			)}
			{showLandSize && (
				<FlatRowFormInput
					control={control}
					name="landSize"
					type="number"
					label={t(PropertyResources.LandArea)}
					placeholder={t(FormResources.Placeholders.fill)}
					suffix={UNITS.squareMeter}
				/>
			)}
			{showDisposition && (
				<FlatRowFormSelect
					control={control}
					name="disposition"
					label={t(PropertyResources.Disposition)}
					options={dispositionOptions}
					placeholder={t(FormResources.Placeholders.select)}
				/>
			)}
			<FlatRowFormSelect
				control={control}
				name="buildingMaterial"
				label={t(PropertyResources.BuildingMaterial)}
				options={buildingMaterialOptions}
				placeholder={t(FormResources.Placeholders.select)}
			/>
			<FlatRowFormInput
				control={control}
				name="numberOfFloors"
				type="number"
				label={t(PropertyResources.NumberOfAboveGroundFloors)}
				placeholder={t(FormResources.Placeholders.fill)}
			/>
			<FlatRowFormSelect
				control={control}
				name="amenities"
				label={t(FormResources.Fields.amenities)}
				options={amenityOptions}
				placeholder={t(FormResources.Placeholders.select)}
				multiple
				isLast
			/>
		</>
	);
};
